import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import { inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { logError, SEVERITY } from '@core-mkt/shared/lib/logger';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';

/**
 * EPS-4801: values of the `data-coupon-scroll-target` attribute a priced component sets on the
 * element the page should scroll back to after an email-capture coupon lands.
 */
export const COUPON_SCROLL_TARGET = {
  pricingTable: 'pricing-table',
  bundle: 'bundle',
  topSection: 'top-section',
} as const;

export type CouponScrollTarget = (typeof COUPON_SCROLL_TARGET)[keyof typeof COUPON_SCROLL_TARGET];

/** Highest priority first (EPS-4801 AC1). */
const COUPON_SCROLL_PRIORITY: CouponScrollTarget[] = [
  COUPON_SCROLL_TARGET.pricingTable,
  COUPON_SCROLL_TARGET.bundle,
  COUPON_SCROLL_TARGET.topSection,
];

/**
 * Where `scrollend` is missing (older Safari/iOS), the scroll counts as settled once no `scroll`
 * event has arrived for this long.
 */
export const COUPON_SCROLL_QUIET_MS = 150;

/** Upper bound on waiting for the scroll to settle, whichever signal the browser offers. */
export const COUPON_SCROLL_SETTLE_TIMEOUT_MS = 2000;

/**
 * A Top Section price already inside this band of the viewport is left where it is: above it a
 * sticky header may cover the price, below it the visitor may not see the roll-down.
 */
const IN_VIEW_TOP_RATIO = 0.2;
const IN_VIEW_BOTTOM_RATIO = 0.9;

/**
 * Scrolls the page back to the highest-priority priced component after an email-capture
 * coupon is applied, and announces when that scroll has settled so a component can start its
 * price roll-down once the visitor can see it.
 *
 * Priority is Pricing Table, then Bundles, then Top Section. A target that isn't rendered is
 * skipped, so a component tags both its mobile and desktop layouts and whichever is visible wins.
 *
 * Pricing Table and Bundles keep `id="bundle"` for Craft `#bundle` links. This service ignores ids
 * and reads only `data-coupon-scroll-target`, so a hand-authored `#bundle` anchor is not a target.
 */
@Injectable({ providedIn: 'root' })
export class CouponScrollService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly zone = inject(NgZone);

  private readonly settled = new Subject<HTMLElement | null>();
  private inFlight: Promise<HTMLElement | null> | null = null;

  /**
   * Emits once per fresh submission, after the scroll settles, with the element scrolled to
   * (`null` when the page has no rendered target). Never emits on page load, so a returning
   * visitor whose coupon is re-broadcast gets no roll-down.
   */
  readonly scrollSettled$: Observable<HTMLElement | null> = this.settled.asObservable();

  /** Whether a scroll started by `scrollToPricedComponent()` has yet to settle. */
  scrollPending(): boolean {
    return this.inFlight !== null;
  }

  /**
   * Scrolls to the highest-priority rendered target, then emits `scrollSettled$`.
   * @returns The element scrolled to, or `null` when the page has no rendered target (and always on the server).
   */
  scrollToPricedComponent(): Promise<HTMLElement | null> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve<HTMLElement | null>(null);
    // A quick double-submit must not start a second scroll or announce a second settle; both
    // callers share the one already moving.
    if (this.inFlight !== null) return this.inFlight;

    const target = this.findTarget();
    this.inFlight = this.scrollTo(target)
      .catch((error: unknown) => {
        // A failed scroll must not leave the page stuck "pending": settle anyway, so the
        // confirmation and the price updates carry on, and surface the failure.
        logError({
          error: error instanceof Error ? error : new Error(String(error)),
          message: 'COUPON_SCROLL_FAILED',
          severity: SEVERITY.WARN,
          tags: ['coupon-scroll'],
        });
      })
      .then(() => {
        this.inFlight = null;
        this.settled.next(target);
        return target;
      });
    return this.inFlight;
  }

  private findTarget(): HTMLElement | null {
    for (const key of COUPON_SCROLL_PRIORITY) {
      const candidates = this.document.querySelectorAll<HTMLElement>(`[data-coupon-scroll-target="${key}"]`);
      const rendered = Array.from(candidates).find((el) => el.getClientRects().length > 0);
      if (rendered) return rendered;
    }
    return null;
  }

  private scrollTo(target: HTMLElement | null): Promise<void> {
    const win = this.document.defaultView;
    if (!target || !win) return Promise.resolve();

    const rect = target.getBoundingClientRect();
    const isTopSection = target.getAttribute('data-coupon-scroll-target') === COUPON_SCROLL_TARGET.topSection;
    // Top Section's price row is small, so if it is already comfortably on screen, scrolling
    // would only move the page under the visitor.
    if (
      isTopSection &&
      rect.top >= win.innerHeight * IN_VIEW_TOP_RATIO &&
      rect.bottom <= win.innerHeight * IN_VIEW_BOTTOM_RATIO
    ) {
      return Promise.resolve();
    }

    // Pricing Table and Bundles top-align their whole container. Top Section's target is the price
    // row itself, which top-aligned would sit under a sticky header, so center it instead.
    const offset = isTopSection ? Math.max(0, (win.innerHeight - rect.height) / 2) : 0;
    const maxTop = Math.max(0, this.document.documentElement.scrollHeight - win.innerHeight);
    const top = Math.min(maxTop, Math.max(0, rect.top + win.scrollY - offset));
    // Already in place (including clamped at the top or bottom of the page): no scroll happens,
    // so no scroll event would ever fire.
    if (Math.abs(top - win.scrollY) < 1) return Promise.resolve();

    // Scroll events and timers only decide when to settle; none change bound state, so they run
    // outside Angular and only the settle re-enters it.
    return new Promise((resolve) => {
      this.zone.runOutsideAngular(() => {
        let quietTimer: ReturnType<typeof setTimeout> | undefined;
        const done = (): void => {
          win.removeEventListener('scrollend', done);
          win.removeEventListener('scroll', onScroll);
          clearTimeout(quietTimer);
          clearTimeout(capTimer);
          this.zone.run(() => resolve());
        };
        const onScroll = (): void => {
          clearTimeout(quietTimer);
          quietTimer = setTimeout(done, COUPON_SCROLL_QUIET_MS);
        };
        const capTimer = setTimeout(done, COUPON_SCROLL_SETTLE_TIMEOUT_MS);
        // Reflect.has, not `in`: Window's type always declares onscrollend, so `in` would narrow the else away.
        if (Reflect.has(win, 'onscrollend')) {
          win.addEventListener('scrollend', done);
        } else {
          win.addEventListener('scroll', onScroll, { passive: true });
        }
        // The global `scroll-behavior: smooth` animates this, except for reduced-motion users, who jump.
        if (this.breakpointObserver.isMatched('(prefers-reduced-motion: reduce)')) {
          this.viewportScroller.scrollToPosition([win.scrollX, top], { behavior: 'instant' });
        } else {
          this.viewportScroller.scrollToPosition([win.scrollX, top]);
        }
      });
    });
  }
}
